import { Fragment, useMemo, useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { mdiArrowLeft, mdiClose, mdiContentSave, mdiPlus } from '@mdi/js'
import { AppIcon } from './ui/icon'
import {
  useContractTypes,
  useContractStakeholderTypes,
  useAsset,
  useCreateContract
} from '../hooks/useQueries'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { useToast } from '../hooks/use-toast'
import { useLocale } from '../contexts/LocaleContext'
import { groupContractTypes, contractTypeLabel } from '../lib/contract-type-tree'
import { UserSelect } from './UserSelect'
import type { ApiSearchableSelectOption } from './ApiSearchableSelect'
import { AssetPicker } from './AssetPicker'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'

interface StakeholderInput {
  stakeholder_type_uid: string
  user_uid: string
  // Captured at selection time (UserSelect's onOptionSelect) rather than
  // re-derived from a client-side user list, which used to silently miss
  // anyone past the API's first page — see
  // specs/todos/2026-07-21-04-user-pickers-truncated-to-first-page.md.
  user_name: string
  user_email: string
}

interface AssetInput {
  asset_uid: string
  // Captured at pick time (AssetPicker's onChange) rather than re-derived
  // from a client-side asset list, which used to silently miss anything
  // beyond the list endpoint's first page — see
  // specs/todos/2026-09-14-05-contract-create-asset-picker.md.
  asset_name: string
}

interface CreateContractPageProps {
  preselectedAssetUid?: string
  /** Prefills the contract type once the types are loaded, if it is one of them. */
  preselectedContractTypeUid?: string
}

export function CreateContractPage({ preselectedAssetUid, preselectedContractTypeUid }: CreateContractPageProps) {
  const { org } = useParams({ strict: false })
  const navigate = useNavigate()
  const { t } = useTranslation('contracts')
  const { toast } = useToast()

  const { locale } = useLocale()
  const { data: contractTypes = [] } = useContractTypes(org || 'demo')
  const contractTypeGroups = useMemo(() => groupContractTypes(contractTypes, locale), [contractTypes, locale])
  const { data: stakeholderTypes = [] } = useContractStakeholderTypes(org || 'demo')
  const { data: preselectedAsset } = useAsset(org || 'demo', preselectedAssetUid || '')
  const createMutation = useCreateContract(org || 'demo')

  // State for form fields
  const [code, setCode] = useState('')
  const [contractTypeUid, setContractTypeUid] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<string>('draft')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  
  // State for stakeholders
  const [stakeholders, setStakeholders] = useState<StakeholderInput[]>([])
  const [newStakeholder, setNewStakeholder] = useState<StakeholderInput>({
    stakeholder_type_uid: '',
    user_uid: '',
    user_name: '',
    user_email: ''
  })

  // State for assets
  const [selectedAssets, setSelectedAssets] = useState<AssetInput[]>([])

  // Auto-add preselected asset when available. Fetched directly by uid
  // (rather than scanning the paginated asset list) so this still works when
  // the asset is beyond page 1 of the portfolio.
  useEffect(() => {
    if (!preselectedAssetUid || preselectedAsset?.uid !== preselectedAssetUid) return

    setSelectedAssets(prev =>
      prev.some(a => a.asset_uid === preselectedAssetUid)
        ? prev
        : [...prev, { asset_uid: preselectedAssetUid, asset_name: preselectedAsset.name || t('detail.asset_unknown') }]
    )
  }, [preselectedAssetUid, preselectedAsset, t])

  // Prefill the contract type once, when the types are loaded and the uid is one of them.
  const [typePrefilled, setTypePrefilled] = useState(false)
  useEffect(() => {
    if (typePrefilled || !preselectedContractTypeUid || contractTypes.length === 0) return
    setTypePrefilled(true)
    if (contractTypes.some(ct => ct.uid === preselectedContractTypeUid)) setContractTypeUid(preselectedContractTypeUid)
  }, [typePrefilled, preselectedContractTypeUid, contractTypes])

  // Set breadcrumbs
  const breadcrumbs = useMemo(() => [
    { title: t('common:nav.home', 'Home'), path: `/${org}` },
    { title: t('list.title'), path: `/${org}/contracts` },
    { title: t('create.title') }
  ], [org, t])

  useBreadcrumbs(breadcrumbs)

  const handleSave = async () => {
    // Validation: at least one stakeholder or asset must be provided
    if (stakeholders.length === 0 && selectedAssets.length === 0) {
      toast({
        title: t('common:status.error'),
        description: t('create.error_missing_stakeholder_or_asset'),
        variant: "destructive"
      })
      return
    }

    try {
      // Build request object, only including fields that have values
      const requestData: any = {
        status: status as 'draft' | 'open' | 'in_progress' | 'closed',
        stakeholders: stakeholders.length > 0 ? stakeholders.map(s => ({
          stakeholder_type_uid: s.stakeholder_type_uid,
          user_uid: s.user_uid
        })) : undefined,
        assets: selectedAssets.length > 0 ? selectedAssets.map(a => ({ asset_uid: a.asset_uid })) : undefined
      }

      // Only include optional fields if they have values
      if (code.trim()) {
        requestData.code = code.trim()
      }
      if (contractTypeUid) {
        requestData.contract_type_uid = contractTypeUid
      }
      if (name.trim()) {
        requestData.name = name.trim()
      }
      if (description.trim()) {
        requestData.description = description.trim()
      }
      if (startDate) {
        requestData.start_date = startDate
      }
      if (endDate) {
        requestData.end_date = endDate
      }

      console.log('Creating contract with data:', requestData)

      const result = await createMutation.mutateAsync(requestData)

      // No success notification: the contract page we land on IS the
      // confirmation. `useToast` is still a native `alert()`, so announcing a
      // success here blocked the redirect behind an OK click — the manager had
      // to dismiss a popup to reach the thing they just created.
      navigate({
        to: '/$org/contracts/$contractUid',
        params: {
          org: org || 'demo',
          contractUid: result.uid || ''
        }
      })
    } catch (error) {
      console.error('Failed to create contract:', error)
      toast({
        title: t('create.error_title'),
        description: t('create.error_failed', { message: error instanceof Error ? error.message : t('common:status.error') }),
        variant: "destructive"
      })
    }
  }

  const addStakeholder = () => {
    if (!newStakeholder.stakeholder_type_uid || !newStakeholder.user_uid) {
      toast({
        title: t('common:status.error'),
        description: t('create.error_stakeholder_incomplete'),
        variant: "destructive"
      })
      return
    }

    setStakeholders([...stakeholders, {
      stakeholder_type_uid: newStakeholder.stakeholder_type_uid,
      user_uid: newStakeholder.user_uid,
      user_name: newStakeholder.user_name,
      user_email: newStakeholder.user_email
    }])

    setNewStakeholder({
      stakeholder_type_uid: '',
      user_uid: '',
      user_name: '',
      user_email: ''
    })
  }

  const removeStakeholder = (index: number) => {
    setStakeholders(stakeholders.filter((_, i) => i !== index))
  }

  const addAsset = (assetUid: string, assetName: string) => {
    if (selectedAssets.some(a => a.asset_uid === assetUid)) {
      toast({
        title: t('common:status.error'),
        description: t('create.error_asset_already_added'),
        variant: "destructive"
      })
      return
    }

    setSelectedAssets([...selectedAssets, { asset_uid: assetUid, asset_name: assetName || t('detail.asset_unknown') }])
  }

  const removeAsset = (assetUid: string) => {
    setSelectedAssets(selectedAssets.filter(a => a.asset_uid !== assetUid))
  }

  const resolveMultilingual = (value: Record<string, string> | string | undefined) => {
    if (!value) return undefined
    if (typeof value === 'string') return value
    return value[locale] || value.en || value.fr || Object.values(value)[0]
  }

  const getStakeholderTypeName = (stakeholderTypeUid: string) => {
    const stakeholderType = stakeholderTypes.find(st => st.uid === stakeholderTypeUid)
    if (!stakeholderType) return t('detail.stakeholder_type_unknown')

    const localized =
      resolveMultilingual((stakeholderType as any).names) ||
      resolveMultilingual(stakeholderType.name)

    return localized || stakeholderType.code || t('detail.stakeholder_type_unknown')
  }

  const getUserName = (stakeholder: StakeholderInput) => {
    return stakeholder.user_name || stakeholder.user_email || t('detail.user_unknown')
  }

  return (
    <div className="container mx-auto py-8 max-w-4xl">
      <div className="space-y-6">
        {/* Header */}
        <PageHeader
          icon={SECTION_ICONS.contracts}
          title={t('create.title')}
          description={t('create.subtitle')}
          actions={
            <Link to="/$org/contracts" params={{ org: org || 'demo' }}>
              <Button variant="ghost">
                <AppIcon path={mdiArrowLeft} size="sm" className="mr-2" />
                {t('create.back')}
              </Button>
            </Link>
          }
        />

        {/* Form */}
        <Card>
          <CardHeader>
            <CardTitle>{t('create.details_title')}</CardTitle>
            <CardDescription>
              {t('create.details_description')}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Contract Code */}
            <div>
              <Label htmlFor="code">{t('create.field_code')}</Label>
              <Input
                id="code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder={t('create.field_code_placeholder')}
                data-testid="code-input"
              />
            </div>

            {/* Contract Type */}
            <div>
              <Label htmlFor="contract-type">{t('create.field_contract_type')}</Label>
              <Select value={contractTypeUid} onValueChange={setContractTypeUid}>
                <SelectTrigger data-testid="contract-type-select">
                  <SelectValue placeholder={t('create.field_contract_type_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {/* Families first, each followed by its indented sub-types.
                      The family stays selectable: a contract typed LEASE with no
                      sub-type is valid. */}
                  {contractTypeGroups.map(({ family, children }) => (
                    <Fragment key={family.uid}>
                      <SelectItem value={family.uid}>
                        {contractTypeLabel(family, locale)}
                      </SelectItem>
                      {children.map((child) => (
                        <SelectItem key={child.uid} value={child.uid} className="pl-12">
                          {contractTypeLabel(child, locale)}
                        </SelectItem>
                      ))}
                    </Fragment>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Title */}
            <div>
              <Label htmlFor="name">{t('create.field_title')}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('create.field_title_placeholder')}
                data-testid="name-input"
              />
            </div>

            {/* Description */}
            <div>
              <Label htmlFor="description">{t('create.field_description')}</Label>
              <Textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('create.field_description_placeholder')}
                rows={4}
                data-testid="description-input"
              />
            </div>

  
            {/* Status and Dates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="status">{t('create.field_status')}</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">{t('status.draft')}</SelectItem>
                    <SelectItem value="open">{t('status.open')}</SelectItem>
                    <SelectItem value="in_progress">{t('status.in_progress')}</SelectItem>
                    <SelectItem value="closed">{t('status.closed')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="start-date">{t('create.field_start_date')}</Label>
                <Input
                  id="start-date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="end-date">{t('create.field_end_date')}</Label>
                <Input
                  id="end-date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stakeholders */}
        <Card>
          <CardHeader>
            <CardTitle>{t('create.stakeholders_title')}</CardTitle>
            <CardDescription>
              {t('create.stakeholders_description')} <span className="text-destructive font-medium">{t('create.stakeholders_required')}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add Stakeholder Form */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 border rounded-lg">
              <Select
                value={newStakeholder.stakeholder_type_uid}
                onValueChange={(value) => setNewStakeholder({ ...newStakeholder, stakeholder_type_uid: value })}
              >
                <SelectTrigger data-testid="create-contract-stakeholder-type-select">
                  <SelectValue placeholder={t('create.stakeholder_type_placeholder')} />
                </SelectTrigger>
                <SelectContent>
                  {stakeholderTypes.map((type) => (
                    <SelectItem key={type.uid} value={type.uid}>
                      {getStakeholderTypeName(type.uid)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <UserSelect
                org={org || 'demo'}
                value={newStakeholder.user_uid}
                onValueChange={(value) => setNewStakeholder({ ...newStakeholder, user_uid: value })}
                onOptionSelect={(option: ApiSearchableSelectOption) => {
                  const data = option.data as { name?: string; email?: string } | undefined
                  setNewStakeholder(prev => ({
                    ...prev,
                    user_uid: option.value,
                    user_name: data?.name || option.label || '',
                    user_email: data?.email || ''
                  }))
                }}
                placeholder={t('create.user_placeholder')}
                data-testid="create-contract-stakeholder-user-select"
              />
              <Button
                onClick={addStakeholder}
                disabled={!newStakeholder.stakeholder_type_uid || !newStakeholder.user_uid}
                data-testid="create-contract-add-stakeholder"
              >
                <AppIcon path={mdiPlus} size="sm" />
              </Button>
            </div>

            {/* Stakeholders List */}
            {stakeholders.length > 0 && (
              <div className="space-y-2">
                {stakeholders.map((stakeholder, index) => (
                  <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <p className="font-medium">{getUserName(stakeholder)}</p>
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <span>{stakeholder.user_email}</span>
                        <span>{t('create.stakeholder_type', { type: getStakeholderTypeName(stakeholder.stakeholder_type_uid) })}</span>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => removeStakeholder(index)}>
                      <AppIcon path={mdiClose} size="sm" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Assets */}
        <Card>
          <CardHeader>
            <CardTitle>{t('create.associated_assets_title')}</CardTitle>
            <CardDescription>
              {t('create.associated_assets_description')} <span className="text-destructive font-medium">{t('create.associated_assets_required')}</span>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <AssetPicker
              org={org || 'demo'}
              onChange={(uid, name) => addAsset(uid, name)}
              placeholder={t('create.asset_select_placeholder')}
              testId="create-contract-asset-picker"
            />

            {/* Selected Assets */}
            {selectedAssets.length > 0 && (
              <div className="space-y-2" data-testid="create-contract-selected-assets">
                {selectedAssets.map((asset) => (
                  <div key={asset.asset_uid} className="flex items-center justify-between p-3 border rounded-lg">
                    <div>
                      <p className="font-medium">{asset.asset_name}</p>
                      <p className="text-sm text-muted-foreground">{t('create.asset_label')}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeAsset(asset.asset_uid)}
                      data-testid={`create-contract-remove-asset-${asset.asset_uid}`}
                    >
                      <AppIcon path={mdiClose} size="sm" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Validation Note */}
        <div className="bg-amber-50 dark:bg-amber-950 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
          <p className="text-sm text-amber-800 dark:text-amber-200">
            <span className="font-medium">{t('create.required_note')}</span>
          </p>
        </div>

        {/* Actions */}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={createMutation.isPending}
            data-testid="create-contract"
          >
            <AppIcon path={mdiContentSave} size="sm" className="mr-2" />
            {t('create.submit')}
          </Button>
        </div>
      </div>
    </div>
  )
}
